import { VoucherCart } from "../voucher_edit/voucher";
import { toRupees } from "../../utils/number";
import { classNames, getPrefixFromClientId } from "../../utils/etc";
import { Match, Show, Switch, createSignal } from "solid-js";
import { ButtonRegular } from "../../widgets/button";
import {
  goBack,
  HubbleRoute,
  popUntilHome,
  pushPage,
} from "../../shared_states/modal";
import {
  PaymentOptions,
  VoucherGenLoader,
  VoucherGenerationFailed,
} from "../payment_options/payment_options";
import { snackApiCallWrapper } from "../../shared_states/snackbar";
import { initPgRedemption } from "../../data/products";
import { RedemptionTransactions } from "../transactions/transactions";
import { TransactionDetail } from "../transactions/transaction_detail";
import { VoucherGenSuccess } from "../payment_options/voucher_gen_success";
import { sdkEventManager, HubbleEvent } from "../../data/events";
import { useClassicContext } from "~/data/classic/classic_context";
import {
  generateDeviceVerificationToken,
  getCoinLabelCapitalized,
  getCoinLabelCapitalizedPluralized,
} from "~/utils/common";
import { captureErrorInSentry } from "~/utils/third_party/sentry";
import { showSnackBar } from "~/shared_states/snackbar";
import { defaultCoin } from "~/assets/assets";
import { getBrandL2SummaryLabel } from "~/data/partner_config";
import { Header } from "~/types";
import { ErrorMessage } from '~/data/consts';

type SummaryModalProps =
  | {
    cart: VoucherCart;
    showUsingCoins: false;
  }
  | {
    cart: VoucherCart;
    showUsingCoins: true;
    coinLabel: string;
  };

function SummaryModalHeader(props: SummaryModalProps) {
  const dryRunTransaction = () =>
    props.cart.dryRunTransaction!.find((r) => r.paymentMode === "UPI")!;

  const { isClientDonationType } = useClassicContext();

  return (
    <>
      <div class="flex items-start justify-between">
        <img
          src={props.cart.voucherProduct!.voucherProductMetadata.logoUrl}
          class="mb-3 h-[72px] w-[72px] rounded-[72px] object-cover"
          alt="brand logo"
        />
      </div>
      <span class="title-header mb-0.5 text-h3 text-textDark">
        {toRupees(props.cart.amount!)} brand card
      </span>
      {/* <Switch>
        <Match when={isClientDonationType()}>
          <span class="text-h5Bold font-normal text-successDark">
            {toRupees(
              dryRunTransaction().discountDetails.hubbleBaseDiscount.amount
            )}
            &nbsp;
            <span>{"will be donated "}</span>
            <span>
              {" "}
              (
              {
                dryRunTransaction().discountDetails.hubbleBaseDiscount
                  .percentage
              }
              % off)
            </span>
            <span>{" on card purchase"}</span>
          </span>
        </Match>
        <Match when={true}>
          <span class="body-normal text-h5Bold font-normal text-successDark">
            Getting for {toRupees(dryRunTransaction().requiredDeposit)}
            &nbsp;
            <span
              class="body-normal text-h5Bold font-normal text-successDark line-through"
              style={{
                // keep the font size same as rest of the content if using coins label is shown
                "font-size": props.showUsingCoins ? "19px" : "15px",
              }}
            >
              {toRupees(dryRunTransaction().redemptionAmount)}
            </span>{" "}
            {props.showUsingCoins ? (
              <span>using {props.coinLabel}</span>
            ) : (
              <span>
                {" "}
                ({dryRunTransaction().discountDetails.totalDiscount.percentage}%
                off)
              </span>
            )}
          </span>
        </Match>
      </Switch> */}
    </>
  );
}

export function SummaryModal(props: { cart: VoucherCart }) {
  const { data, isClientDonationType } = useClassicContext();

  const dryRunTransaction = () =>
    props.cart.dryRunTransaction!.find((r) => r.paymentMode === "UPI")!;
  const discountDetails = () => dryRunTransaction().discountDetails;
  const totalRequiredCoins = () => dryRunTransaction().totalRequiredCoins;
  const coinToInrConversionRatio = data.isLoggedIn
    ? (data.clientConfig.coinToInrConversionRatio ?? 1)
    : 1;
  const coinIconUrl = data.isLoggedIn
    ? data.clientConfig.coinIconUrl
    : defaultCoin;

  const v4Home = data.isLoggedIn ? data.v4HomePage : null;
  const fraudChecksEnabled = v4Home?.user.fraudChecksEnabled;

  const [isGenerating, setIsGenerating] = createSignal(false);

  const useCoinsToFundBaseDiscount = data.isLoggedIn
    ? data.clientConfig.useCoinsToFundBaseDiscount
    : false;

  const clientId = data.clientId;

  return (
    <div class="flex flex-col bg-baseTertiaryLight p-5">
      <SummaryModalHeader
        cart={props.cart}
        showUsingCoins={useCoinsToFundBaseDiscount}
        coinLabel={getCoinLabelCapitalizedPluralized(clientId)}
      />
      <div class="px-0 py-4">
        <div class="caption-overline mb-4 text-f12w700 uppercase text-textNormal">
          Summary
        </div>
        <div class="mb-2.5 h-px border-t border-y-baseTertiaryMedium" />
        <SummaryItem
          label="Brand card"
          value={toRupees(dryRunTransaction().redemptionAmount)}
        />
        <Switch>
          <Match
            when={
              useCoinsToFundBaseDiscount &&
              discountDetails().hubbleBaseDiscount.amount > 0
            }
          >
            <SummaryItemRichCoinConsumption
              clientId={clientId}
              coinIconUrl={coinIconUrl}
              discountAmount={discountDetails().hubbleBaseDiscount.amount}
              totalRequiredCoins={totalRequiredCoins()}
              coinToInrConversionRatio={coinToInrConversionRatio}
            />
          </Match>
          <Match
            when={
              discountDetails().hubbleBaseDiscount.amount > 0 &&
              !isClientDonationType()
            }
          >
            <SummaryItem
              class="font-semibold text-successDark"
              label={`${getBrandL2SummaryLabel(getPrefixFromClientId(clientId))} (${discountDetails().hubbleBaseDiscount.percentage}%)`}
              value={toRupees(discountDetails().hubbleBaseDiscount.amount)}
            />
          </Match>
        </Switch>

        <Show
          when={dryRunTransaction().discountDetails.couponDiscount.amount > 0}
        >
          <SummaryItem
            class="text-bold text-successDark"
            label={`"${props.cart.couponCode}" applied (${dryRunTransaction().discountDetails.couponDiscount.percentage}%)`}
            value={
              "-" +
              toRupees(
                dryRunTransaction().discountDetails.couponDiscount.amount
              )
            }
          />
        </Show>

        <Show when={discountDetails().coinDiscount.amount > 0}>
          <SummaryItem
            class="text-bold text-successDark"
            label={`${getCoinLabelCapitalizedPluralized(clientId)} discount (${discountDetails().coinDiscount.percentage}%)`}
            value={"-" + toRupees(discountDetails().coinDiscount.amount)}
          />
        </Show>
      </div>
      <Show when={dryRunTransaction().dueAmount > 0}>
        <div class="mb-2.5 h-px border-t border-y-baseTertiaryMedium" />
        <SummaryItem
          class="text-bold text-textDark"
          label="You pay only"
          value={toRupees(dryRunTransaction().dueAmount)}
        />
      </Show>

      <Switch>
        <Match when={dryRunTransaction().dueAmount > 0}>
          <ButtonRegular
            onClick={() => {
              goBack(() => {
                pushPage(
                  () => <PaymentOptions cart={props.cart} />,
                  undefined,
                  HubbleRoute.PAYMENT_OPTIONS
                );
              });
            }}
            isEnabled={true}
            class={"button-filled mt-5"}
          >
            Pay {toRupees(dryRunTransaction().dueAmount)}
          </ButtonRegular>
        </Match>
        <Match when={true}>
          <ButtonRegular
            isLoading={isGenerating()}
            onClick={async () => {
              setIsGenerating(true);
              let headers = undefined;
              if (fraudChecksEnabled) {
                const token = await generateDeviceVerificationToken();
                if (!token) {
                  captureErrorInSentry(new Error('FPJS token is undefined'));
                  showSnackBar({
                    message: ErrorMessage.SECURITY_CHECK_FAILED,
                    level: 'error',
                  });
                  setIsGenerating(false);
                  return;
                }
                headers = { [Header.FpDeviceToken]: token };
              }

              const res = await snackApiCallWrapper(
                initPgRedemption({
                  ...(headers ? { headers } : {}),
                  params: {
                    amount: dryRunTransaction().redemptionAmount,
                    paymentMode: dryRunTransaction().paymentMode,
                    productId: props.cart.voucherProductId,
                    denominationDetails: props.cart.denominations?.map((d) => {
                      return {
                        denomination: d.denomination,
                        quantity: d.quantity,
                      };
                    }),
                    variantId: props.cart.variantId,
                    consumeCoins: props.cart.useCoins,
                    couponCode: props.cart.couponCode,
                    coinsToConsume: props.cart.coinsToConsume,
                  },
                })
              );

              setIsGenerating(false);
              sdkEventManager.sendEvent(HubbleEvent.COIN_PAYMENT, {
                amount: dryRunTransaction().redemptionAmount,
                coins: totalRequiredCoins(),
                baseDiscount: discountDetails().hubbleBaseDiscount.amount,
                couponDiscount: discountDetails().couponDiscount.amount,
                totalDiscount: discountDetails().totalDiscount.amount,
              });
              goBack(() => {
                pushPage(
                  () => (
                    <VoucherGenLoader
                      onVoucherGenerated={async (order, donationDetails) => {
                        if (order.status == "COMPLETED") {
                          await popUntilHome();
                          pushPage(
                            () => <RedemptionTransactions />,
                            undefined,
                            HubbleRoute.TRANSACTIONS
                          );
                          pushPage(
                            () => (
                              <TransactionDetail
                                transactionId={res.id}
                                type="redemption"
                              />
                            ),
                            undefined,
                            HubbleRoute.TRANSACTION_L2
                          );
                          pushPage(
                            () => (
                              <VoucherGenSuccess
                                orderId={order.id}
                                orderDetails={order}
                                onClose={() => goBack()}
                                refreshProfile={true}
                                donationDetails={donationDetails}
                              />
                            ),
                            undefined,
                            HubbleRoute.VOUCHER_GENERATION_SUCCESS
                          );
                        }

                        if (order.status == "FAILED") {
                          goBack(() => {
                            pushPage(
                              () => <VoucherGenerationFailed />,
                              undefined,
                              HubbleRoute.VOUCHER_GENERATION_FAIL
                            );
                          });
                        }
                      }}
                      orderId={res.storeOrderDetails.id}
                      transactionId={res.id}
                    />
                  ),
                  undefined,
                  HubbleRoute.VOUCHER_GENERATION_LOADER
                );
              });
            }}
            isEnabled={true}
            class={"button-filled mt-5"}
          >
            Get the card
          </ButtonRegular>
        </Match>
      </Switch>
    </div>
  );
}

export function SummaryItem(props: {
  label: string;
  value: string;
  class?: string;
  isCoin?: boolean;
}) {
  return (
    <div
      class={classNames(
        "title-primary flex flex-row justify-between px-0 py-1.5 text-basePrimaryDark",
        props.class
      )}
    >
      <span>{props.label}</span>
      <div
        class={
          props.isCoin
            ? "flex items-center justify-center gap-0.5 text-coinPercentageOrange"
            : ""
        }
      >
        <Show when={props.isCoin}>
          <img class="inline h-4 w-4" src={defaultCoin} alt="" />
        </Show>{" "}
        {props.value}
      </div>
    </div>
  );
}

type SummaryItemRichCoinConsumptionProps = {
  clientId: string;
  coinIconUrl: string;
  discountAmount: number;
  totalRequiredCoins: number;
  coinToInrConversionRatio: number;
};

function SummaryItemRichCoinConsumption(
  props: SummaryItemRichCoinConsumptionProps
) {
  return (
    <div class="flex flex-row justify-between">
      <div class="flex flex-col gap-1">
        <div class="flex items-center text-normal text-successDark">
          {getCoinLabelCapitalized(props.clientId)} discount (
          <img class="inline h-5 w-5" src={props.coinIconUrl} alt="" />
          {props.totalRequiredCoins})
        </div>
        <div class="flex items-center gap-1 text-medium text-textNormal">
          <img class="inline h-5 w-5" src={props.coinIconUrl} alt="" />
          <span>
            {1 / props.coinToInrConversionRatio}{" "}
            {getCoinLabelCapitalizedPluralized(props.clientId)} = ₹1
          </span>
        </div>
      </div>
      <div class="text-normal text-successDark">
        -{toRupees(props.discountAmount)}
      </div>
    </div>
  );
}
